import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import {
  Bell,
  BookOpen,
  BookCopy,
  Bookmark,
  Check,
  Copy,
  Download,
  Eye,
  EyeOff,
  Globe,
  Image as ImageIcon,
  Layers,
  MoreVertical,
  Pencil,
  Plus,
  QrCode,
  Trash2,
  Undo2,
  Upload,
  Users,
  XCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY_STATUSES = [
  { value: 'beschikbaar', label: 'Beschikbaar', className: 'bg-emerald-100 text-emerald-700' },
  { value: 'uitgeleend', label: 'Uitgeleend', className: 'bg-blue-100 text-blue-700' },
  { value: 'gereserveerd', label: 'Gereserveerd', className: 'bg-indigo-100 text-indigo-700' },
  { value: 'kwijt', label: 'Kwijt', className: 'bg-red-100 text-red-700' },
  { value: 'beschadigd', label: 'Beschadigd', className: 'bg-amber-100 text-amber-700' },
  { value: 'buiten_gebruik', label: 'Buiten gebruik', className: 'bg-slate-200 text-slate-600' },
];

const LOAN_STATUSES = [
  { value: 'uitgeleend', label: 'Uitgeleend', className: 'bg-blue-100 text-blue-700' },
  { value: 'inlevering_gemeld', label: 'Inlevering gemeld', className: 'bg-amber-100 text-amber-800' },
  { value: 'ingeleverd', label: 'Ingeleverd', className: 'bg-slate-100 text-slate-500' },
  { value: 'te_laat', label: 'Te laat', className: 'bg-red-100 text-red-700' },
  { value: 'kwijt', label: 'Kwijt', className: 'bg-red-100 text-red-700' },
];

const RESERVATION_STATUSES = [
  { value: 'wachtend', label: 'Wachtend', className: 'bg-amber-100 text-amber-700' },
  { value: 'klaar_om_op_te_halen', label: 'Klaar om op te halen', className: 'bg-blue-100 text-blue-700' },
  { value: 'voltooid', label: 'Voltooid', className: 'bg-emerald-100 text-emerald-700' },
  { value: 'geannuleerd', label: 'Geannuleerd', className: 'bg-slate-100 text-slate-500' },
  { value: 'verlopen', label: 'Verlopen', className: 'bg-red-100 text-red-700' },
];

const REQUEST_TYPES = [
  { value: 'reservation', label: 'Reservering', className: 'bg-indigo-50 text-indigo-700' },
  { value: 'loan_request', label: 'Leenaanvraag', className: 'bg-teal-50 text-teal-700' },
];

const EMPTY_PUBLIC_SETTINGS_FORM = {
  enabled: false,
  allow_guest_requests: true,
  show_unavailable_books: true,
  show_shelf_location: false,
  max_active_requests_per_email: '3',
  contact_note: '',
};

const EMPTY_CATEGORY_FORM = { name: '', description: '', visible_to_members: true };
const EMPTY_AUTHOR_FORM = { name: '', description: '', website: '' };
const EMPTY_BOOK_FORM = {
  title: '', subtitle: '', author_id: '', category_id: '', isbn: '', publisher: '',
  publish_year: '', language: '', description: '', tags: '', target_audience: '', notes: '',
  cover_image_url: '', is_digital: false,
};

const MAX_EBOOK_SIZE_BYTES = 25 * 1024 * 1024;
const ALLOWED_EBOOK_TYPES = ['application/pdf', 'application/epub+zip'];

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const MAX_COVER_SIZE_BYTES = 2 * 1024 * 1024;
const ALLOWED_COVER_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});
const EMPTY_COPY_FORM = { copy_number: '', barcode: '', location: '', condition: '', status: 'beschikbaar', acquired_at: '', source: '', notes: '' };
const EMPTY_LOAN_FORM = { book_id: '', copy_id: '', borrower_name: '', borrower_email: '', notes: '' };
const EMPTY_RESERVATION_FORM = { book_id: '', requester_name: '', requester_email: '', notes: '' };

const getLabel = (options, value) => options.find((option) => option.value === value)?.label || value || '-';
const getClassName = (options, value) => options.find((option) => option.value === value)?.className || 'bg-slate-100 text-slate-700';

const LibraryManagementPage = () => {
  const { hasCapability } = useAuth();
  const canManage = hasCapability('library_manage');

  const [activeTab, setActiveTab] = useState('books');
  const [categories, setCategories] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [books, setBooks] = useState([]);
  const [copies, setCopies] = useState([]);
  const [loans, setLoans] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState(EMPTY_CATEGORY_FORM);

  const [authorDialogOpen, setAuthorDialogOpen] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [authorForm, setAuthorForm] = useState(EMPTY_AUTHOR_FORM);

  const [bookDialogOpen, setBookDialogOpen] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [bookForm, setBookForm] = useState(EMPTY_BOOK_FORM);
  const coverFileInputRef = useRef(null);
  const ebookFileInputRef = useRef(null);
  const [ebookUploading, setEbookUploading] = useState(false);

  const [copiesDialogBook, setCopiesDialogBook] = useState(null);
  const [editingCopy, setEditingCopy] = useState(null);
  const [copyForm, setCopyForm] = useState(EMPTY_COPY_FORM);

  const [loanDialogOpen, setLoanDialogOpen] = useState(false);
  const [loanForm, setLoanForm] = useState(EMPTY_LOAN_FORM);

  const [reservationDialogOpen, setReservationDialogOpen] = useState(false);
  const [reservationForm, setReservationForm] = useState(EMPTY_RESERVATION_FORM);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState(null);

  const [orgSlug, setOrgSlug] = useState('');
  const [publicSettingsOpen, setPublicSettingsOpen] = useState(false);
  const [publicSettingsForm, setPublicSettingsForm] = useState(EMPTY_PUBLIC_SETTINGS_FORM);
  const [catalogQrDataUrl, setCatalogQrDataUrl] = useState('');
  const [returnQrDataUrl, setReturnQrDataUrl] = useState('');

  const [bookQrDialogBook, setBookQrDialogBook] = useState(null);
  const [bookQrDataUrl, setBookQrDataUrl] = useState('');

  useEffect(() => {
    fetchAll();
    axios.get(`${API}/organization/branding`).then((res) => setOrgSlug(res.data?.slug || '')).catch(() => {});
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [categoriesRes, authorsRes, booksRes, copiesRes, loansRes, reservationsRes, dashboardRes] = await Promise.all([
        axios.get(`${API}/library/categories`),
        axios.get(`${API}/library/authors`),
        axios.get(`${API}/library/books`),
        axios.get(`${API}/library/copies`),
        axios.get(`${API}/library/loans`),
        axios.get(`${API}/library/reservations`),
        axios.get(`${API}/library/dashboard`),
      ]);
      setCategories(categoriesRes.data);
      setAuthors(authorsRes.data);
      setBooks(booksRes.data);
      setCopies(copiesRes.data);
      setLoans(loansRes.data);
      setReservations(reservationsRes.data);
      setDashboard(dashboardRes.data);
    } catch (error) {
      toast.error('Kon de bibliotheek niet laden');
    } finally {
      setLoading(false);
    }
  };

  const refreshBooksAndCopies = async () => {
    const [booksRes, copiesRes, dashboardRes] = await Promise.all([
      axios.get(`${API}/library/books`),
      axios.get(`${API}/library/copies`),
      axios.get(`${API}/library/dashboard`),
    ]);
    setBooks(booksRes.data);
    setCopies(copiesRes.data);
    setDashboard(dashboardRes.data);
  };

  const refreshLoansAndReservations = async () => {
    const [loansRes, reservationsRes, copiesRes, dashboardRes] = await Promise.all([
      axios.get(`${API}/library/loans`),
      axios.get(`${API}/library/reservations`),
      axios.get(`${API}/library/copies`),
      axios.get(`${API}/library/dashboard`),
    ]);
    setLoans(loansRes.data);
    setReservations(reservationsRes.data);
    setCopies(copiesRes.data);
    setDashboard(dashboardRes.data);
  };

  const bookTitle = (bookId) => books.find((b) => b.id === bookId)?.title || 'Onbekend boek';
  const authorName = (authorId) => authors.find((a) => a.id === authorId)?.name;
  const categoryName = (categoryId) => categories.find((c) => c.id === categoryId)?.name;
  const copyLabel = (copy) => `${bookTitle(copy.book_id)}${copy.copy_number ? ` · #${copy.copy_number}` : ''}`;

  // ---------- Categorieën ----------
  const openCreateCategory = () => { setEditingCategory(null); setCategoryForm(EMPTY_CATEGORY_FORM); setCategoryDialogOpen(true); };
  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({ name: category.name || '', description: category.description || '', visible_to_members: Boolean(category.visible_to_members) });
    setCategoryDialogOpen(true);
  };
  const submitCategory = async (event) => {
    event.preventDefault();
    if (!categoryForm.name) { toast.error('Naam is verplicht'); return; }
    try {
      if (editingCategory) {
        await axios.put(`${API}/library/categories/${editingCategory.id}`, categoryForm);
        toast.success('Categorie bijgewerkt');
      } else {
        await axios.post(`${API}/library/categories`, categoryForm);
        toast.success('Categorie toegevoegd');
      }
      setCategoryDialogOpen(false);
      const response = await axios.get(`${API}/library/categories`);
      setCategories(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Actie mislukt');
    }
  };
  const deleteCategory = async (category) => {
    if (!window.confirm(`Categorie "${category.name}" verwijderen?`)) return;
    try {
      await axios.delete(`${API}/library/categories/${category.id}`);
      toast.success('Categorie verwijderd');
      setCategories((prev) => prev.filter((c) => c.id !== category.id));
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  // ---------- Auteurs ----------
  const openCreateAuthor = () => { setEditingAuthor(null); setAuthorForm(EMPTY_AUTHOR_FORM); setAuthorDialogOpen(true); };
  const openEditAuthor = (author) => {
    setEditingAuthor(author);
    setAuthorForm({ name: author.name || '', description: author.description || '', website: author.website || '' });
    setAuthorDialogOpen(true);
  };
  const submitAuthor = async (event) => {
    event.preventDefault();
    if (!authorForm.name) { toast.error('Naam is verplicht'); return; }
    try {
      if (editingAuthor) {
        await axios.put(`${API}/library/authors/${editingAuthor.id}`, authorForm);
        toast.success('Auteur bijgewerkt');
      } else {
        await axios.post(`${API}/library/authors`, authorForm);
        toast.success('Auteur toegevoegd');
      }
      const response = await axios.get(`${API}/library/authors`);
      setAuthors(response.data);
      setEditingAuthor(null);
      setAuthorForm(EMPTY_AUTHOR_FORM);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Actie mislukt');
    }
  };
  const deleteAuthor = async (author) => {
    if (!window.confirm(`Auteur "${author.name}" verwijderen?`)) return;
    try {
      await axios.delete(`${API}/library/authors/${author.id}`);
      toast.success('Auteur verwijderd');
      setAuthors((prev) => prev.filter((a) => a.id !== author.id));
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  // ---------- Boeken ----------
  const openCreateBook = () => { setEditingBook(null); setBookForm(EMPTY_BOOK_FORM); setBookDialogOpen(true); };
  const openEditBook = (book) => {
    setEditingBook(book);
    setBookForm({
      title: book.title || '',
      subtitle: book.subtitle || '',
      author_id: book.author_id || '',
      category_id: book.category_id || '',
      isbn: book.isbn || '',
      publisher: book.publisher || '',
      publish_year: book.publish_year != null ? String(book.publish_year) : '',
      language: book.language || '',
      description: book.description || '',
      tags: (book.tags || []).join(', '),
      target_audience: book.target_audience || '',
      notes: book.notes || '',
      cover_image_url: book.cover_image_url || '',
      is_digital: !!book.is_digital,
    });
    setBookDialogOpen(true);
  };
  const handleEbookFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !editingBook) return;
    if (!ALLOWED_EBOOK_TYPES.includes(file.type)) {
      toast.error('Gebruik PDF of EPUB');
      return;
    }
    if (file.size > MAX_EBOOK_SIZE_BYTES) {
      toast.error('Bestand is te groot. Maximaal 25 MB toegestaan.');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    setEbookUploading(true);
    try {
      const response = await axios.post(`${API}/library/books/${editingBook.id}/ebook`, formData);
      setEditingBook(response.data);
      setBookForm((current) => ({ ...current, is_digital: true }));
      toast.success('E-book geüpload');
      refreshBooksAndCopies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Uploaden mislukt');
    } finally {
      setEbookUploading(false);
    }
  };
  const removeEbookFile = async () => {
    if (!editingBook || !window.confirm('E-bookbestand verwijderen?')) return;
    try {
      await axios.delete(`${API}/library/books/${editingBook.id}/ebook`);
      setEditingBook((current) => ({ ...current, ebook_filename: null, ebook_size_bytes: null, is_digital: false }));
      setBookForm((current) => ({ ...current, is_digital: false }));
      toast.success('E-book verwijderd');
      refreshBooksAndCopies();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };
  const handleCoverFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!ALLOWED_COVER_TYPES.includes(file.type)) {
      toast.error('Gebruik PNG, JPG of WEBP');
      return;
    }
    if (file.size > MAX_COVER_SIZE_BYTES) {
      toast.error('Afbeelding is te groot. Maximaal 2 MB toegestaan.');
      return;
    }
    const dataUrl = await readFileAsDataUrl(file);
    setBookForm((current) => ({ ...current, cover_image_url: dataUrl }));
  };
  const submitBook = async (event) => {
    event.preventDefault();
    if (!bookForm.title) { toast.error('Titel is verplicht'); return; }
    const payload = {
      ...bookForm,
      author_id: bookForm.author_id || null,
      category_id: bookForm.category_id || null,
      publish_year: bookForm.publish_year ? parseInt(bookForm.publish_year, 10) : null,
      tags: bookForm.tags ? bookForm.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
    };
    try {
      if (editingBook) {
        await axios.put(`${API}/library/books/${editingBook.id}`, payload);
        toast.success('Boek bijgewerkt');
      } else {
        await axios.post(`${API}/library/books`, payload);
        toast.success('Boek toegevoegd');
      }
      setBookDialogOpen(false);
      refreshBooksAndCopies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Actie mislukt');
    }
  };
  const deleteBook = async (book) => {
    if (!window.confirm(`Boek "${book.title}" en al zijn exemplaren verwijderen?`)) return;
    try {
      await axios.delete(`${API}/library/books/${book.id}`);
      toast.success('Boek verwijderd');
      refreshBooksAndCopies();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  const togglePublished = async (book) => {
    try {
      await axios.put(`${API}/library/books/${book.id}`, { published: !book.published });
      toast.success(book.published ? 'Boek verborgen voor de publieke bibliotheek' : 'Boek gepubliceerd naar de publieke bibliotheek');
      refreshBooksAndCopies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Actie mislukt');
    }
  };

  const publicCatalogUrl = orgSlug ? `${window.location.origin}/${orgSlug}/bibliotheek` : '';
  const publicReturnUrl = orgSlug ? `${window.location.origin}/${orgSlug}/bibliotheek/inleveren` : '';
  const publicBookUrl = (book) => (orgSlug ? `${window.location.origin}/${orgSlug}/bibliotheek/boek/${book.slug}` : '');
  const bookQrUrl = (book) => (orgSlug ? `${window.location.origin}/${orgSlug}/bibliotheek/b/${book.id}` : '');

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link gekopieerd');
  };

  const downloadQr = (dataUrl, filename) => {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // ---------- Publieke bibliotheek ----------
  const openPublicSettingsDialog = async () => {
    try {
      const response = await axios.get(`${API}/library/public-settings`);
      setPublicSettingsForm({
        ...response.data,
        max_active_requests_per_email: response.data.max_active_requests_per_email != null ? String(response.data.max_active_requests_per_email) : '',
        contact_note: response.data.contact_note || '',
      });
      setPublicSettingsOpen(true);
      if (publicCatalogUrl) {
        QRCode.toDataURL(publicCatalogUrl, { width: 240, margin: 1 }).then(setCatalogQrDataUrl).catch(() => {});
      }
      if (publicReturnUrl) {
        QRCode.toDataURL(publicReturnUrl, { width: 240, margin: 1 }).then(setReturnQrDataUrl).catch(() => {});
      }
    } catch (error) {
      toast.error('Kon instellingen niet laden');
    }
  };
  const submitPublicSettings = async (event) => {
    event.preventDefault();
    try {
      await axios.put(`${API}/library/public-settings`, {
        ...publicSettingsForm,
        max_active_requests_per_email: publicSettingsForm.max_active_requests_per_email
          ? parseInt(publicSettingsForm.max_active_requests_per_email, 10) : null,
        contact_note: publicSettingsForm.contact_note || null,
      });
      toast.success('Instellingen opgeslagen');
    } catch (error) {
      toast.error('Opslaan mislukt');
    }
  };

  const openBookQrDialog = async (book) => {
    setBookQrDialogBook(book);
    const url = bookQrUrl(book);
    if (url) {
      const dataUrl = await QRCode.toDataURL(url, { width: 240, margin: 1 });
      setBookQrDataUrl(dataUrl);
    }
  };

  // ---------- Exemplaren ----------
  const copiesForBook = (bookId) => copies.filter((c) => c.book_id === bookId);
  const openCopiesDialog = (book) => { setCopiesDialogBook(book); setEditingCopy(null); setCopyForm(EMPTY_COPY_FORM); };
  const openEditCopy = (copy) => {
    setEditingCopy(copy);
    setCopyForm({
      copy_number: copy.copy_number || '', barcode: copy.barcode || '', location: copy.location || '',
      condition: copy.condition || '', status: copy.status || 'beschikbaar', acquired_at: copy.acquired_at || '',
      source: copy.source || '', notes: copy.notes || '',
    });
  };
  const submitCopy = async (event) => {
    event.preventDefault();
    if (!copiesDialogBook) return;
    try {
      if (editingCopy) {
        await axios.put(`${API}/library/copies/${editingCopy.id}`, copyForm);
        toast.success('Exemplaar bijgewerkt');
      } else {
        await axios.post(`${API}/library/copies`, { ...copyForm, book_id: copiesDialogBook.id });
        toast.success('Exemplaar toegevoegd');
      }
      setEditingCopy(null);
      setCopyForm(EMPTY_COPY_FORM);
      await refreshBooksAndCopies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Actie mislukt');
    }
  };
  const deleteCopy = async (copy) => {
    if (!window.confirm('Dit exemplaar verwijderen?')) return;
    try {
      await axios.delete(`${API}/library/copies/${copy.id}`);
      toast.success('Exemplaar verwijderd');
      refreshBooksAndCopies();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  // ---------- Uitleningen ----------
  const availableCopiesForBook = (bookId) => copiesForBook(bookId).filter((c) => c.status === 'beschikbaar');
  const openCreateLoan = () => { setLoanForm(EMPTY_LOAN_FORM); setLoanDialogOpen(true); };
  const submitLoan = async (event) => {
    event.preventDefault();
    if (!loanForm.copy_id || !loanForm.borrower_name) { toast.error('Exemplaar en naam lener zijn verplicht'); return; }
    try {
      await axios.post(`${API}/library/loans`, {
        copy_id: loanForm.copy_id,
        borrower_name: loanForm.borrower_name,
        borrower_email: loanForm.borrower_email || null,
        notes: loanForm.notes || null,
      });
      toast.success('Boek uitgeleend');
      setLoanDialogOpen(false);
      refreshLoansAndReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Actie mislukt');
    }
  };
  const returnLoan = async (loan) => {
    if (!window.confirm(`${loan.status === 'inlevering_gemeld' ? 'Je bevestigt dat het boek fysiek is gecontroleerd. ' : ''}Boek van ${loan.borrower_name} als ingeleverd markeren?`)) return;
    try {
      await axios.post(`${API}/library/loans/${loan.id}/return`);
      toast.success('Boek ingeleverd');
      refreshLoansAndReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Actie mislukt');
    }
  };

  // ---------- Reserveringen ----------
  const openCreateReservation = () => { setReservationForm(EMPTY_RESERVATION_FORM); setReservationDialogOpen(true); };
  const submitReservation = async (event) => {
    event.preventDefault();
    if (!reservationForm.book_id || !reservationForm.requester_name) { toast.error('Boek en naam zijn verplicht'); return; }
    try {
      await axios.post(`${API}/library/reservations`, {
        book_id: reservationForm.book_id,
        requester_name: reservationForm.requester_name,
        requester_email: reservationForm.requester_email || null,
        notes: reservationForm.notes || null,
      });
      toast.success('Reservering toegevoegd');
      setReservationDialogOpen(false);
      refreshLoansAndReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Actie mislukt');
    }
  };
  const cancelReservation = async (reservation) => {
    if (!window.confirm(`Reservering van ${reservation.requester_name} annuleren?`)) return;
    try {
      await axios.post(`${API}/library/reservations/${reservation.id}/cancel`);
      toast.success('Reservering geannuleerd');
      refreshLoansAndReservations();
    } catch (error) {
      toast.error('Annuleren mislukt');
    }
  };
  const approveReservation = async (reservation) => {
    try {
      await axios.post(`${API}/library/reservations/${reservation.id}/approve`);
      toast.success('Leenaanvraag goedgekeurd, boek is uitgeleend');
      refreshLoansAndReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Goedkeuren mislukt');
    }
  };

  // ---------- Herinneringen instellen ----------
  const openSettingsDialog = async () => {
    try {
      const response = await axios.get(`${API}/library/notification-settings`);
      setSettingsForm({ ...response.data, days_before_due: response.data.days_before_due.join(', ') });
      setSettingsOpen(true);
    } catch (error) {
      toast.error('Kon instellingen niet laden');
    }
  };
  const parseDaysList = (value) => value.split(',').map((item) => parseInt(item.trim(), 10)).filter((n) => !Number.isNaN(n));
  const submitSettings = async (event) => {
    event.preventDefault();
    try {
      await axios.put(`${API}/library/notification-settings`, {
        ...settingsForm,
        loan_period_days: parseInt(settingsForm.loan_period_days, 10) || 28,
        days_before_due: parseDaysList(settingsForm.days_before_due),
      });
      toast.success('Instellingen opgeslagen');
      setSettingsOpen(false);
    } catch (error) {
      toast.error('Opslaan mislukt');
    }
  };

  const dashboardCards = useMemo(() => ([
    { label: 'Boeken', value: dashboard?.total_books ?? 0, icon: BookOpen, color: 'text-teal-600' },
    { label: 'Exemplaren beschikbaar', value: `${dashboard?.available_copies ?? 0} / ${dashboard?.total_copies ?? 0}`, icon: Layers, color: 'text-emerald-600' },
    { label: 'Actieve uitleningen', value: dashboard?.active_loans ?? 0, icon: BookCopy, color: 'text-blue-600' },
    { label: 'Te laat', value: dashboard?.overdue_loans ?? 0, icon: Undo2, color: 'text-red-500' },
    { label: 'Reserveringen', value: dashboard?.pending_reservations ?? 0, icon: Bookmark, color: 'text-indigo-500' },
  ]), [dashboard]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-56"></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5].map((item) => (
              <div key={item} className="h-24 bg-slate-200 rounded-2xl"></div>
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="library-management-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Bibliotheek</h1>
            <p className="text-slate-500">Boekentafel en kerkbibliotheek: boeken, uitleningen en reserveringen</p>
          </div>
          {canManage && (
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Button variant="outline" onClick={openPublicSettingsDialog} className="rounded-xl w-full sm:w-auto">
                <Globe className="w-4 h-4 mr-2" />
                Publieke bibliotheek
              </Button>
              <Button variant="outline" onClick={openSettingsDialog} className="rounded-xl w-full sm:w-auto">
                <Bell className="w-4 h-4 mr-2" />
                Herinneringen instellen
              </Button>
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {dashboardCards.map(({ label, value, icon: Icon, color }) => (
            <Card key={label} className="border-0 shadow-sm">
              <CardContent className="p-5 flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{label}</p>
                  <p className="text-2xl font-bold text-slate-900">{value}</p>
                </div>
                <Icon className={`w-8 h-8 ${color}`} />
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto">
            <TabsList>
              <TabsTrigger value="books"><BookOpen className="w-4 h-4 mr-2" />Boeken</TabsTrigger>
              <TabsTrigger value="categories"><Layers className="w-4 h-4 mr-2" />Categorieën</TabsTrigger>
              <TabsTrigger value="loans"><BookCopy className="w-4 h-4 mr-2" />Uitleningen</TabsTrigger>
              <TabsTrigger value="reservations"><Bookmark className="w-4 h-4 mr-2" />Reserveringen</TabsTrigger>
            </TabsList>
          </div>

          {/* BOEKEN */}
          <TabsContent value="books" className="space-y-4">
            {canManage && (
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="outline" onClick={() => setAuthorDialogOpen(true)} className="rounded-xl">
                  <Users className="w-4 h-4 mr-2" />
                  Auteurs beheren
                </Button>
                <Button onClick={openCreateBook} className="rounded-xl bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Nieuw boek
                </Button>
              </div>
            )}
            {books.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-12 text-center">
                  <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">Nog geen boeken</h3>
                  <p className="text-slate-500">Voeg het eerste boek van de boekentafel of kerkbibliotheek toe.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid xl:grid-cols-2 gap-4">
                {books.map((book) => (
                  <Card key={book.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4 min-w-0">
                          <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center shrink-0 overflow-hidden">
                            {book.cover_image_url
                              ? <img src={book.cover_image_url} alt="" className="w-full h-full object-cover" />
                              : <BookOpen className="w-6 h-6" />}
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold text-slate-900 truncate">{book.title}</h3>
                            <p className="text-sm text-slate-500 truncate">{authorName(book.author_id) || 'Onbekende auteur'}</p>
                          </div>
                        </div>
                        {canManage && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openCopiesDialog(book)}>
                                <Layers className="w-4 h-4 mr-2" />
                                Exemplaren beheren
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openEditBook(book)}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Bewerken
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => togglePublished(book)}>
                                {book.published ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                                {book.published ? 'Verbergen' : 'Publiceren'}
                              </DropdownMenuItem>
                              {book.published && orgSlug && (
                                <>
                                  <DropdownMenuItem onClick={() => window.open(publicBookUrl(book), '_blank')}>
                                    <Globe className="w-4 h-4 mr-2" />
                                    Bekijk publieke pagina
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openBookQrDialog(book)}>
                                    <QrCode className="w-4 h-4 mr-2" />
                                    QR-code
                                  </DropdownMenuItem>
                                </>
                              )}
                              <DropdownMenuItem onClick={() => deleteBook(book)} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" />
                                Verwijderen
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 mt-4">
                        {book.category_id && (
                          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">
                            {categoryName(book.category_id)}
                          </span>
                        )}
                        {book.is_digital ? (
                          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700">
                            Digitaal{book.ebook_filename ? '' : ' — geen bestand'}
                          </span>
                        ) : (
                          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700">
                            {book.copy_available ?? 0} van {book.copy_total ?? 0} beschikbaar
                          </span>
                        )}
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${book.published ? 'bg-teal-50 text-teal-700' : 'bg-slate-100 text-slate-500'}`}>
                          {book.published ? 'Gepubliceerd' : 'Niet gepubliceerd'}
                        </span>
                      </div>

                      {!canManage && (
                        <Button variant="outline" size="sm" className="mt-4 rounded-xl" onClick={() => openCopiesDialog(book)}>
                          Exemplaren bekijken
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* CATEGORIEEN */}
          <TabsContent value="categories" className="space-y-4">
            {canManage && (
              <div className="flex justify-end">
                <Button onClick={openCreateCategory} className="rounded-xl bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Nieuwe categorie
                </Button>
              </div>
            )}
            <Card className="border-0 shadow-sm">
              <CardContent className="p-0">
                {categories.length === 0 ? (
                  <div className="p-6 text-sm text-slate-500">Nog geen categorieën toegevoegd.</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {categories.map((category) => (
                      <div key={category.id} className="flex items-center gap-4 p-4">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-900">{category.name}</div>
                          {category.description && <div className="text-sm text-slate-500">{category.description}</div>}
                        </div>
                        {canManage && (
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openEditCategory(category)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteCategory(category)} className="text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* UITLENINGEN */}
          <TabsContent value="loans" className="space-y-4">
            {canManage && (
              <div className="flex justify-end">
                <Button onClick={openCreateLoan} className="rounded-xl bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Nieuwe uitlening
                </Button>
              </div>
            )}
            <Card className="border-0 shadow-sm">
              <CardContent className="p-0">
                {loans.length === 0 ? (
                  <div className="p-6 text-sm text-slate-500">Nog geen uitleningen geregistreerd.</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {loans.map((loan) => (
                      <div key={loan.id} className="flex items-center gap-4 p-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-slate-900">{loan.borrower_name}</span>
                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${getClassName(LOAN_STATUSES, loan.status)}`}>
                              {getLabel(LOAN_STATUSES, loan.status)}
                            </span>
                            {loan.checkout_source === 'public_qr' && <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-teal-50 text-teal-700">Via QR</span>}
                            {loan.status === 'inlevering_gemeld' && <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-amber-50 text-amber-800">Controleer in boekenkast</span>}
                          </div>
                          <div className="text-sm text-slate-500 mt-0.5">
                            {copyLabel({ book_id: copies.find((c) => c.id === loan.copy_id)?.book_id, copy_number: copies.find((c) => c.id === loan.copy_id)?.copy_number })}
                            {' · '}Uitgeleend op {loan.loan_date} · Uiterlijk terug op {loan.due_date}
                            {loan.borrower_email ? ` · ${loan.borrower_email}` : ''}
                          </div>
                        </div>
                        {canManage && loan.status !== 'ingeleverd' && (
                          <Button variant="outline" size="sm" onClick={() => returnLoan(loan)} className="rounded-xl">
                            <Undo2 className="w-4 h-4 mr-2" />
                            Markeer ingeleverd
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* RESERVERINGEN */}
          <TabsContent value="reservations" className="space-y-4">
            {canManage && (
              <div className="flex justify-end">
                <Button onClick={openCreateReservation} className="rounded-xl bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Nieuwe reservering
                </Button>
              </div>
            )}
            <Card className="border-0 shadow-sm">
              <CardContent className="p-0">
                {reservations.length === 0 ? (
                  <div className="p-6 text-sm text-slate-500">Nog geen reserveringen.</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {reservations.map((reservation) => (
                      <div key={reservation.id} className="flex items-center gap-4 p-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-slate-900">{reservation.requester_name}</span>
                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${getClassName(RESERVATION_STATUSES, reservation.status)}`}>
                              {getLabel(RESERVATION_STATUSES, reservation.status)}
                            </span>
                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${getClassName(REQUEST_TYPES, reservation.request_type)}`}>
                              {getLabel(REQUEST_TYPES, reservation.request_type)}
                            </span>
                          </div>
                          <div className="text-sm text-slate-500 mt-0.5">
                            {bookTitle(reservation.book_id)} · Gereserveerd op {reservation.reservation_date}
                            {reservation.expires_at ? ` · Klaarliggend tot ${reservation.expires_at}` : ''}
                          </div>
                        </div>
                        {canManage && reservation.request_type === 'loan_request' && reservation.status === 'wachtend' && (
                          <Button variant="outline" size="sm" onClick={() => approveReservation(reservation)} className="rounded-xl text-teal-700 border-teal-200 hover:bg-teal-50">
                            <Check className="w-4 h-4 mr-2" />
                            Goedkeuren
                          </Button>
                        )}
                        {canManage && ['wachtend', 'klaar_om_op_te_halen'].includes(reservation.status) && (
                          <Button variant="ghost" size="sm" onClick={() => cancelReservation(reservation)} className="text-red-600">
                            <XCircle className="w-4 h-4 mr-2" />
                            Annuleren
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Boek dialoog */}
        <Dialog open={bookDialogOpen} onOpenChange={setBookDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingBook ? 'Boek bewerken' : 'Boek toevoegen'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submitBook} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Titel *</Label>
                  <Input value={bookForm.title} onChange={(e) => setBookForm({ ...bookForm, title: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Ondertitel</Label>
                  <Input value={bookForm.subtitle} onChange={(e) => setBookForm({ ...bookForm, subtitle: e.target.value })} className="rounded-xl" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Omslagafbeelding</Label>
                <div className="flex items-start gap-3">
                  <div className="w-16 h-20 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0 overflow-hidden">
                    {bookForm.cover_image_url
                      ? <img src={bookForm.cover_image_url} alt="Omslag" className="w-full h-full object-cover" />
                      : <ImageIcon className="w-6 h-6 text-slate-300" />}
                  </div>
                  <div className="flex-1 space-y-2">
                    <Input
                      value={bookForm.cover_image_url?.startsWith('data:') ? '' : bookForm.cover_image_url}
                      onChange={(e) => setBookForm({ ...bookForm, cover_image_url: e.target.value })}
                      placeholder="https://... (link naar een afbeelding)"
                      className="rounded-xl"
                    />
                    <div className="flex items-center gap-2">
                      <input
                        ref={coverFileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={handleCoverFileChange}
                        className="hidden"
                      />
                      <Button type="button" variant="outline" size="sm" onClick={() => coverFileInputRef.current?.click()} className="rounded-lg">
                        <Upload className="w-4 h-4 mr-2" />
                        Upload vanaf laptop
                      </Button>
                      {bookForm.cover_image_url && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => setBookForm({ ...bookForm, cover_image_url: '' })} className="rounded-lg text-slate-500">
                          Verwijderen
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-slate-400">PNG, JPG of WEBP, maximaal 2 MB.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2 rounded-xl border border-slate-200 p-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={bookForm.is_digital}
                    onChange={(e) => setBookForm({ ...bookForm, is_digital: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm font-medium">Dit is een digitaal werk (e-book, geen fysieke exemplaren)</span>
                </label>
                {bookForm.is_digital && (
                  <div className="pl-6 space-y-2">
                    {editingBook ? (
                      <>
                        {editingBook.ebook_filename ? (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <span>{editingBook.ebook_filename} ({formatFileSize(editingBook.ebook_size_bytes)})</span>
                            <Button type="button" variant="ghost" size="sm" onClick={removeEbookFile} className="rounded-lg text-slate-500">
                              Verwijderen
                            </Button>
                          </div>
                        ) : (
                          <p className="text-xs text-slate-400">Nog geen bestand geüpload.</p>
                        )}
                        <input
                          ref={ebookFileInputRef}
                          type="file"
                          accept="application/pdf,application/epub+zip"
                          onChange={handleEbookFileChange}
                          className="hidden"
                        />
                        <Button type="button" variant="outline" size="sm" onClick={() => ebookFileInputRef.current?.click()} disabled={ebookUploading} className="rounded-lg">
                          <Upload className="w-4 h-4 mr-2" />
                          {ebookUploading ? 'Uploaden…' : editingBook.ebook_filename ? 'Vervangen' : 'Upload PDF of EPUB'}
                        </Button>
                        <p className="text-xs text-slate-400">Maximaal 25 MB. Wordt direct downloadbaar in de publieke bibliotheek.</p>
                      </>
                    ) : (
                      <p className="text-xs text-slate-400">Sla het boek eerst op, upload daarna het bestand door het opnieuw te bewerken.</p>
                    )}
                  </div>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Auteur</Label>
                  <Select value={bookForm.author_id || 'none'} onValueChange={(v) => setBookForm({ ...bookForm, author_id: v === 'none' ? '' : v })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Geen auteur gekoppeld" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Geen auteur gekoppeld</SelectItem>
                      {authors.map((author) => (
                        <SelectItem key={author.id} value={author.id}>{author.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Categorie</Label>
                  <Select value={bookForm.category_id || 'none'} onValueChange={(v) => setBookForm({ ...bookForm, category_id: v === 'none' ? '' : v })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Geen categorie" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Geen categorie</SelectItem>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>ISBN</Label>
                  <Input value={bookForm.isbn} onChange={(e) => setBookForm({ ...bookForm, isbn: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Uitgever</Label>
                  <Input value={bookForm.publisher} onChange={(e) => setBookForm({ ...bookForm, publisher: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Jaar van uitgave</Label>
                  <Input type="number" value={bookForm.publish_year} onChange={(e) => setBookForm({ ...bookForm, publish_year: e.target.value })} className="rounded-xl" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Taal</Label>
                  <Input value={bookForm.language} onChange={(e) => setBookForm({ ...bookForm, language: e.target.value })} placeholder="Bijv. Nederlands" className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Doelgroep</Label>
                  <Input value={bookForm.target_audience} onChange={(e) => setBookForm({ ...bookForm, target_audience: e.target.value })} placeholder="Bijv. volwassenen, jeugd, kinderen" className="rounded-xl" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Thema's / tags (komma-gescheiden)</Label>
                <Input value={bookForm.tags} onChange={(e) => setBookForm({ ...bookForm, tags: e.target.value })} placeholder="Bijv. gebed, discipelschap" className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>Omschrijving</Label>
                <Textarea value={bookForm.description} onChange={(e) => setBookForm({ ...bookForm, description: e.target.value })} className="rounded-xl resize-none" rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Opmerkingen</Label>
                <Textarea value={bookForm.notes} onChange={(e) => setBookForm({ ...bookForm, notes: e.target.value })} className="rounded-xl resize-none" rows={2} />
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setBookDialogOpen(false)} className="rounded-xl">Annuleren</Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">{editingBook ? 'Opslaan' : 'Toevoegen'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Categorie dialoog */}
        <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingCategory ? 'Categorie bewerken' : 'Categorie toevoegen'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submitCategory} className="space-y-4">
              <div className="space-y-2">
                <Label>Naam *</Label>
                <Input value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>Omschrijving</Label>
                <Textarea value={categoryForm.description} onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} className="rounded-xl resize-none" rows={2} />
              </div>
              <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={categoryForm.visible_to_members}
                  onChange={(e) => setCategoryForm({ ...categoryForm, visible_to_members: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                />
                Zichtbaar voor leden/bezoekers
              </label>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)} className="rounded-xl">Annuleren</Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">{editingCategory ? 'Opslaan' : 'Toevoegen'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Auteurs beheren dialoog */}
        <Dialog open={authorDialogOpen} onOpenChange={(open) => { setAuthorDialogOpen(open); if (!open) { setEditingAuthor(null); setAuthorForm(EMPTY_AUTHOR_FORM); } }}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Auteurs beheren</DialogTitle>
            </DialogHeader>
            <form onSubmit={submitAuthor} className="space-y-3 border-b border-slate-100 pb-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <Input placeholder="Naam *" value={authorForm.name} onChange={(e) => setAuthorForm({ ...authorForm, name: e.target.value })} className="rounded-xl" />
                <Input placeholder="Website" value={authorForm.website} onChange={(e) => setAuthorForm({ ...authorForm, website: e.target.value })} className="rounded-xl" />
              </div>
              <Textarea placeholder="Omschrijving" value={authorForm.description} onChange={(e) => setAuthorForm({ ...authorForm, description: e.target.value })} className="rounded-xl resize-none" rows={2} />
              <div className="flex justify-end gap-2">
                {editingAuthor && (
                  <Button type="button" variant="outline" onClick={() => { setEditingAuthor(null); setAuthorForm(EMPTY_AUTHOR_FORM); }} className="rounded-xl">
                    Annuleren
                  </Button>
                )}
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                  {editingAuthor ? 'Opslaan' : 'Auteur toevoegen'}
                </Button>
              </div>
            </form>
            <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
              {authors.length === 0 ? (
                <p className="py-4 text-sm text-slate-500">Nog geen auteurs toegevoegd.</p>
              ) : (
                authors.map((author) => (
                  <div key={author.id} className="flex items-center gap-3 py-2">
                    <span className="flex-1 text-sm text-slate-900">{author.name}</span>
                    <Button variant="ghost" size="sm" onClick={() => openEditAuthor(author)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteAuthor(author)} className="text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Exemplaren beheren dialoog */}
        <Dialog open={Boolean(copiesDialogBook)} onOpenChange={(open) => { if (!open) { setCopiesDialogBook(null); setEditingCopy(null); setCopyForm(EMPTY_COPY_FORM); } }}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Exemplaren — {copiesDialogBook?.title}</DialogTitle>
            </DialogHeader>
            {canManage && (
              <form onSubmit={submitCopy} className="space-y-3 border-b border-slate-100 pb-4">
                <div className="grid sm:grid-cols-3 gap-3">
                  <Input placeholder="Exemplaarnummer" value={copyForm.copy_number} onChange={(e) => setCopyForm({ ...copyForm, copy_number: e.target.value })} className="rounded-xl" />
                  <Input placeholder="Barcode" value={copyForm.barcode} onChange={(e) => setCopyForm({ ...copyForm, barcode: e.target.value })} className="rounded-xl" />
                  <Input placeholder="Locatie" value={copyForm.location} onChange={(e) => setCopyForm({ ...copyForm, location: e.target.value })} className="rounded-xl" />
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <Input placeholder="Conditie" value={copyForm.condition} onChange={(e) => setCopyForm({ ...copyForm, condition: e.target.value })} className="rounded-xl" />
                  <Select value={copyForm.status} onValueChange={(v) => setCopyForm({ ...copyForm, status: v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {COPY_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input placeholder="Herkomst (bijv. gift, aankoop)" value={copyForm.source} onChange={(e) => setCopyForm({ ...copyForm, source: e.target.value })} className="rounded-xl" />
                </div>
                <div className="flex justify-end gap-2">
                  {editingCopy && (
                    <Button type="button" variant="outline" onClick={() => { setEditingCopy(null); setCopyForm(EMPTY_COPY_FORM); }} className="rounded-xl">
                      Annuleren
                    </Button>
                  )}
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                    {editingCopy ? 'Opslaan' : 'Exemplaar toevoegen'}
                  </Button>
                </div>
              </form>
            )}
            <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
              {copiesDialogBook && copiesForBook(copiesDialogBook.id).length === 0 ? (
                <p className="py-4 text-sm text-slate-500">Nog geen exemplaren van dit boek.</p>
              ) : (
                copiesDialogBook && copiesForBook(copiesDialogBook.id).map((copy) => (
                  <div key={copy.id} className="flex items-center gap-3 py-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-900">{copy.copy_number || copy.id.slice(0, 8)}</span>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${getClassName(COPY_STATUSES, copy.status)}`}>
                          {getLabel(COPY_STATUSES, copy.status)}
                        </span>
                      </div>
                      {copy.location && <div className="text-xs text-slate-400">{copy.location}</div>}
                    </div>
                    {canManage && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => openEditCopy(copy)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteCopy(copy)} className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Uitlening dialoog */}
        <Dialog open={loanDialogOpen} onOpenChange={setLoanDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Nieuwe uitlening</DialogTitle>
            </DialogHeader>
            <form onSubmit={submitLoan} className="space-y-4">
              <div className="space-y-2">
                <Label>Boek *</Label>
                <Select value={loanForm.book_id} onValueChange={(v) => setLoanForm({ ...loanForm, book_id: v, copy_id: '' })}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Kies een boek" /></SelectTrigger>
                  <SelectContent>
                    {books.map((book) => (
                      <SelectItem key={book.id} value={book.id} disabled={availableCopiesForBook(book.id).length === 0}>
                        {book.title} ({availableCopiesForBook(book.id).length} beschikbaar)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Exemplaar *</Label>
                <Select value={loanForm.copy_id} onValueChange={(v) => setLoanForm({ ...loanForm, copy_id: v })} disabled={!loanForm.book_id}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Kies een exemplaar" /></SelectTrigger>
                  <SelectContent>
                    {availableCopiesForBook(loanForm.book_id).map((copy) => (
                      <SelectItem key={copy.id} value={copy.id}>{copy.copy_number || copy.id.slice(0, 8)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Naam lener *</Label>
                  <Input value={loanForm.borrower_name} onChange={(e) => setLoanForm({ ...loanForm, borrower_name: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>E-mail lener</Label>
                  <Input type="email" value={loanForm.borrower_email} onChange={(e) => setLoanForm({ ...loanForm, borrower_email: e.target.value })} placeholder="Voor herinneringen" className="rounded-xl" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Opmerkingen</Label>
                <Textarea value={loanForm.notes} onChange={(e) => setLoanForm({ ...loanForm, notes: e.target.value })} className="rounded-xl resize-none" rows={2} />
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setLoanDialogOpen(false)} className="rounded-xl">Annuleren</Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">Uitlenen</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Reservering dialoog */}
        <Dialog open={reservationDialogOpen} onOpenChange={setReservationDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Nieuwe reservering</DialogTitle>
            </DialogHeader>
            <form onSubmit={submitReservation} className="space-y-4">
              <div className="space-y-2">
                <Label>Boek *</Label>
                <Select value={reservationForm.book_id} onValueChange={(v) => setReservationForm({ ...reservationForm, book_id: v })}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Kies een boek" /></SelectTrigger>
                  <SelectContent>
                    {books.map((book) => <SelectItem key={book.id} value={book.id}>{book.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Naam *</Label>
                  <Input value={reservationForm.requester_name} onChange={(e) => setReservationForm({ ...reservationForm, requester_name: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>E-mail</Label>
                  <Input type="email" value={reservationForm.requester_email} onChange={(e) => setReservationForm({ ...reservationForm, requester_email: e.target.value })} placeholder="Voor melding als het klaarligt" className="rounded-xl" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Opmerkingen</Label>
                <Textarea value={reservationForm.notes} onChange={(e) => setReservationForm({ ...reservationForm, notes: e.target.value })} className="rounded-xl resize-none" rows={2} />
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setReservationDialogOpen(false)} className="rounded-xl">Annuleren</Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">Reserveren</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Herinneringen instellen dialoog */}
        <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Herinneringen instellen</DialogTitle>
            </DialogHeader>
            {settingsForm && (
              <form onSubmit={submitSettings} className="space-y-4">
                <div className="space-y-2">
                  <Label>Standaard uitleentermijn (dagen)</Label>
                  <Input type="number" min="1" value={settingsForm.loan_period_days} onChange={(e) => setSettingsForm({ ...settingsForm, loan_period_days: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Dagen vóór inleverdatum melden (komma-gescheiden)</Label>
                  <Input value={settingsForm.days_before_due} onChange={(e) => setSettingsForm({ ...settingsForm, days_before_due: e.target.value })} placeholder="3" className="rounded-xl" />
                </div>
                <div className="grid gap-3">
                  <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={settingsForm.notify_on_overdue}
                      onChange={(e) => setSettingsForm({ ...settingsForm, notify_on_overdue: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    />
                    Lener e-mailen bij een te laat boek
                  </label>
                  <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={settingsForm.notify_on_reservation_ready}
                      onChange={(e) => setSettingsForm({ ...settingsForm, notify_on_reservation_ready: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    />
                    Aanvrager e-mailen als reservering klaarligt
                  </label>
                  <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={settingsForm.notify_staff_on_public_loan !== false}
                      onChange={(e) => setSettingsForm({ ...settingsForm, notify_staff_on_public_loan: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    />
                    Bibliotheekverantwoordelijken e-mailen bij directe QR-uitleen
                  </label>
                  <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={settingsForm.notify_staff_on_return_report !== false}
                      onChange={(e) => setSettingsForm({ ...settingsForm, notify_staff_on_return_report: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    />
                    Bibliotheekverantwoordelijken e-mailen bij gemelde QR-inlevering
                  </label>
                </div>
                <DialogFooter className="gap-2">
                  <Button type="button" variant="outline" onClick={() => setSettingsOpen(false)} className="rounded-xl">Annuleren</Button>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">Opslaan</Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Publieke bibliotheek delen + instellingen */}
        <Dialog open={publicSettingsOpen} onOpenChange={setPublicSettingsOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Publieke bibliotheek</DialogTitle>
            </DialogHeader>
            <form onSubmit={submitPublicSettings} className="space-y-4">
              <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={publicSettingsForm.enabled}
                  onChange={(e) => setPublicSettingsForm({ ...publicSettingsForm, enabled: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                />
                Bibliotheek publiek zichtbaar
              </label>

              {publicSettingsForm.enabled && orgSlug && (
                <div className="rounded-xl border border-slate-200 p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <code className="text-xs text-slate-600 truncate">{publicCatalogUrl}</code>
                    <div className="flex gap-2 shrink-0">
                      <Button type="button" variant="outline" size="sm" onClick={() => window.open(publicCatalogUrl, '_blank')} className="rounded-lg">
                        <Globe className="w-4 h-4" />
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => copyToClipboard(publicCatalogUrl)} className="rounded-lg">
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {catalogQrDataUrl && (
                    <div className="flex flex-col items-center gap-2 pt-2">
                      <img src={catalogQrDataUrl} alt="QR-code publieke bibliotheek" className="rounded-lg" />
                      <Button type="button" variant="outline" size="sm" onClick={() => downloadQr(catalogQrDataUrl, 'bibliotheek-qr.png')} className="rounded-lg">
                        <Download className="w-4 h-4 mr-2" />
                        QR-code downloaden
                      </Button>
                      <p className="max-w-sm text-center text-xs text-slate-500">Print deze QR-code en plak hem bij de boekenkast. Bezoekers openen hiermee de publieke bibliotheek en registreren zelf welk boek zij meenemen.</p>
                    </div>
                  )}
                  {returnQrDataUrl && (
                    <div className="mt-4 flex flex-col items-center gap-2 border-t border-slate-100 pt-5">
                      <p className="font-semibold text-slate-800">Inlever-QR</p>
                      <img src={returnQrDataUrl} alt="QR-code boek inleveren" className="rounded-lg" />
                      <Button type="button" variant="outline" size="sm" onClick={() => downloadQr(returnQrDataUrl, 'bibliotheek-inleveren-qr.png')} className="rounded-lg"><Download className="mr-2 h-4 w-4" />Inlever-QR downloaden</Button>
                      <p className="max-w-sm text-center text-xs text-slate-500">Plak deze QR-code bij de plek waar boeken worden teruggelegd. Een melding maakt het boek nog niet beschikbaar; eerst controleert een beheerder de fysieke inlevering.</p>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label>Contactnotitie op de publieke pagina</Label>
                <Textarea
                  value={publicSettingsForm.contact_note}
                  onChange={(e) => setPublicSettingsForm({ ...publicSettingsForm, contact_note: e.target.value })}
                  placeholder="Bijv. Vragen? Spreek de boekentafel-vrijwilliger aan."
                  className="rounded-xl resize-none"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Maximum aantal openstaande aanvragen per e-mailadres</Label>
                <Input
                  type="number"
                  min="1"
                  value={publicSettingsForm.max_active_requests_per_email}
                  onChange={(e) => setPublicSettingsForm({ ...publicSettingsForm, max_active_requests_per_email: e.target.value })}
                  className="rounded-xl"
                />
              </div>

              <div className="grid gap-3">
                <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={publicSettingsForm.allow_guest_requests}
                    onChange={(e) => setPublicSettingsForm({ ...publicSettingsForm, allow_guest_requests: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  Bezoekers mogen reserveren/lenen zonder account
                </label>
                <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={publicSettingsForm.show_unavailable_books}
                    onChange={(e) => setPublicSettingsForm({ ...publicSettingsForm, show_unavailable_books: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  Toon ook uitgeleende boeken
                </label>
                <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={publicSettingsForm.show_shelf_location}
                    onChange={(e) => setPublicSettingsForm({ ...publicSettingsForm, show_shelf_location: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  Toon kastlocatie van beschikbare exemplaren
                </label>
              </div>

              <p className="text-xs text-slate-400">
                De naam van de huidige lener wordt nooit publiek getoond. Alleen boeken die je hierboven per boek publiceert, verschijnen op de publieke pagina.
              </p>

              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setPublicSettingsOpen(false)} className="rounded-xl">Sluiten</Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">Opslaan</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Boek QR-code dialoog */}
        <Dialog open={Boolean(bookQrDialogBook)} onOpenChange={(open) => { if (!open) { setBookQrDialogBook(null); setBookQrDataUrl(''); } }}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>QR-code — {bookQrDialogBook?.title}</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center gap-3">
              {bookQrDataUrl ? (
                <img src={bookQrDataUrl} alt={`QR-code voor ${bookQrDialogBook?.title}`} className="rounded-lg" />
              ) : (
                <div className="h-60 w-60 bg-slate-100 rounded-lg animate-pulse" />
              )}
              <p className="text-xs text-slate-400 text-center">
                Deze code blijft geldig, ook als de boektitel later wordt aangepast. Plak 'm op het boek of de kast.
              </p>
              {bookQrDataUrl && bookQrDialogBook && (
                <Button
                  variant="outline"
                  onClick={() => downloadQr(bookQrDataUrl, `${bookQrDialogBook.slug || bookQrDialogBook.id}-qr.png`)}
                  className="rounded-xl w-full"
                >
                  <Download className="w-4 h-4 mr-2" />
                  QR-code downloaden
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default LibraryManagementPage;
